import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState, useEffect, useRef } from "react";
import { ExpenseClaim } from "@/types/expense";
import { toast } from "sonner";
import { getReceiptTypeConfig } from "@/config/receipt-types";
import { Badge } from "@/components/ui/badge";
import { base64ToDataUrl } from "@/utils/image";
import { receiptTypeService, ReceiptTypeDto } from "@/services/receiptTypeService";
import { useDataMode } from "@/providers/DataModeProvider";

interface EditExpenseClaimDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  expense: ExpenseClaim;
  onSave: (expense: ExpenseClaim) => void;
}

export function EditExpenseClaimDialog({
  open,
  onOpenChange,
  expense,
  onSave,
}: EditExpenseClaimDialogProps) {
  const { isLiveMode } = useDataMode();
  const [receiptTypes, setReceiptTypes] = useState<ReceiptTypeDto[]>([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);
  const prevOpen = useRef(false);
  
  const [formData, setFormData] = useState({
    expenseType: expense.expenseType || "aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa",
    supplier: expense.supplier || "",
    amount: expense.amount?.toString() || "",
    transactionDate: expense.transactionDate || new Date().toISOString().split('T')[0],
    description: expense.description || "",
  });

  // Reset form data when dialog opens with a new expense
  useEffect(() => {
    const justOpened = open && !prevOpen.current;
    if (justOpened) {
      setFormData({
        expenseType: expense.expenseType || "aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa",
        supplier: expense.supplier || "",
        amount: expense.amount?.toString() || "",
        transactionDate: expense.transactionDate
          ? new Date(expense.transactionDate).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
        description: expense.description || "",
      });
    }
    prevOpen.current = open;
  }, [open, expense.id]);

  // Fetch receipt types from API
  useEffect(() => {
    const fetchReceiptTypes = async () => {
      try {
        setIsLoadingTypes(true);
        const types = await receiptTypeService.getAll(isLiveMode);
        setReceiptTypes(types);
      } catch (error) {
        console.error('Failed to fetch receipt types:', error);
        toast.error('Failed to load receipt types');
      } finally {
        setIsLoadingTypes(false);
      }
    };

    if (open) {
      fetchReceiptTypes();
    }
  }, [open, isLiveMode]);

  const handleSave = () => {
    // Validation
    if (!formData.supplier.trim()) {
      toast.error("Supplier name is required");
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }

    if (!formData.expenseType) {
      toast.error("Expense type is required");
      return;
    }

    // Create updated expense claim
    const updatedExpense: ExpenseClaim = {
      ...expense,
      expenseType: formData.expenseType,
      supplier: formData.supplier,
      amount: parseFloat(formData.amount),
      transactionDate: formData.transactionDate,
      description: formData.description,
      updatedAt: new Date().toISOString(),
    };

    onSave(updatedExpense);
  };

  const handleCancel = () => {
    // Reset form to original values
    setFormData({
      expenseType: expense.expenseType || "aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa",
      supplier: expense.supplier || "",
      amount: expense.amount?.toString() || "",
      transactionDate: expense.transactionDate || new Date().toISOString().split('T')[0],
      description: expense.description || "",
    });
    onOpenChange(false);
  };

  const selectedTypeConfig = 
    receiptTypes.find(t => t.id === formData.expenseType) ||
    getReceiptTypeConfig(formData.expenseType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Expense Claim</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          {/* Receipt Image Preview */}
          <div className="space-y-4">
            <Label>Receipt Image</Label>
            <div className="border rounded-lg overflow-hidden bg-muted/20">
              <img
                src={base64ToDataUrl(expense.receiptImageData)}
                alt="Receipt"
                className="w-full h-auto"
              />
            </div>
          </div>

          {/* Edit Form */}
          <div className="space-y-4">
            {/* Expense Type */}
            <div className="space-y-2">
              <Label htmlFor="expenseType">
                Expense Type <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.expenseType}
                onValueChange={(value) =>
                  setFormData({ ...formData, expenseType: value })
                }
                disabled={isLoadingTypes}
              >
                <SelectTrigger id="expenseType">
                  <SelectValue>
                    {isLoadingTypes ? (
                      "Loading..."
                    ) : selectedTypeConfig ? (
                      <div className="flex items-center gap-2">
                        <Badge 
                          className={`${selectedTypeConfig.color} ${selectedTypeConfig.textColor} border-0`}
                        >
                          {selectedTypeConfig.name === 'OfficeSupplies' ? 'Office Supplies' : selectedTypeConfig.name}
                        </Badge>
                      </div>
                    ) : (
                      "Select type"
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {receiptTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      <div className="flex items-center gap-2">
                        <Badge 
                          className={`${type.color} ${type.textColor} border-0`}
                        >
                          {type.name === 'OfficeSupplies' ? 'Office Supplies' : type.name}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Supplier */}
            <div className="space-y-2">
              <Label htmlFor="supplier">
                Merchant/Supplier <span className="text-destructive">*</span>
              </Label>
              <Input
                id="supplier"
                value={formData.supplier}
                onChange={(e) =>
                  setFormData({ ...formData, supplier: e.target.value })
                }
                placeholder="Enter merchant name"
              />
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="amount">
                Amount <span className="text-destructive">*</span>
              </Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) =>
                  setFormData({ ...formData, amount: e.target.value })
                }
                placeholder="0.00"
              />
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label htmlFor="date">
                Transaction Date <span className="text-destructive">*</span>
              </Label>
              <Input
                id="date"
                type="date"
                value={formData.transactionDate}
                onChange={(e) =>
                  setFormData({ ...formData, transactionDate: e.target.value })
                }
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Additional Comments</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Add notes or description"
                rows={4}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={handleCancel} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSave} className="flex-1">
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
